"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Lock, Phone, Sparkles, UserPlus, X } from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import CommonAuth from "../auth/CommonAuth";
import useToastNotification from "@/hook/useToastNotification";
import { useAppDispatch } from "@/store/hooks";
import { LeadPlatform, submitLead } from "@/store/feature/enquiry/leadApi";
import { sendCustomGaEvent } from "@/scripts/ga";

type FollowUpTab = "contact" | "signup";

interface AnonymousFollowUpModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAnonymous: boolean;
  initialTab?: FollowUpTab;
  hasExceededLimit?: boolean;
  platform?: LeadPlatform;
  source: string;
  contactButtonText?: string;
  closeOnContactSuccess?: boolean;
  onContactSuccess?: () => void;
  data?: Record<string, unknown>;
}

const normalizePhoneNumber = (phone: string) => phone.replace(/[^\d+]/g, "");
const isValidPhoneNumber = (phone: string) => /^\+?[1-9]\d{6,14}$/.test(phone);
const isValidFullName = (name: string) =>
  /^[A-Za-z]+(?:[ ][A-Za-z]+)*$/.test(name);

const splitFullName = (name: string) => {
  const trimmedName = name.trim().replace(/\s+/g, " ");
  const parts = trimmedName.split(" ");

  if (parts.length === 1) {
    return {
      firstName: parts[0],
      lastName: "",
    };
  }

  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
};

const AnonymousFollowUpModal = ({
  isOpen,
  setIsOpen,
  isAnonymous,
  initialTab = "contact",
  hasExceededLimit = false,
  platform = "chatbot",
  source,
  contactButtonText = "Unlock Chat",
  closeOnContactSuccess = true,
  onContactSuccess,
  data,
}: AnonymousFollowUpModalProps) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotification();
  const pathname = usePathname();

  const [activeTab, setActiveTab] = useState<FollowUpTab>("contact");
  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [queryString, setQueryString] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    setQueryString(window.location.search.replace(/^\?/, ""));
  }, []);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(hasExceededLimit ? "signup" : initialTab);
      setFullNameError("");
      setPhoneError("");
    }
  }, [hasExceededLimit, initialTab, isOpen]);

  useEffect(() => {
    if (!isAnonymous || !isOpen) return;
    void sendCustomGaEvent("anonymous_followup_modal_view", {
      source,
      platform,
      initial_tab: hasExceededLimit ? "signup" : initialTab,
      has_exceeded_limit: hasExceededLimit,
      page_path: pathname,
      query: queryString || undefined,
    });
  }, [
    hasExceededLimit,
    initialTab,
    isAnonymous,
    isOpen,
    pathname,
    platform,
    queryString,
    source,
  ]);

  if (!isAnonymous || !isOpen) return null;

  const submitLeadFromModal = async () => {
    const normalizedFullName = fullName.trim().replace(/\s+/g, " ");
    const normalizedPhone = normalizePhoneNumber(phone.trim());

    if (!normalizedFullName) {
      setFullNameError("Full name is required");
      return;
    }

    if (!isValidFullName(normalizedFullName)) {
      setFullNameError("Please enter a valid full name");
      return;
    }

    if (!normalizedPhone) {
      setPhoneError("Phone number is required");
      return;
    }

    if (!isValidPhoneNumber(normalizedPhone)) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    const { firstName, lastName } = splitFullName(normalizedFullName);

    setFullNameError("");
    setPhoneError("");
    setIsSubmittingLead(true);

    try {
      await dispatch(
        submitLead({
          phone: normalizedPhone,
          platform,
          data: {
            source,
            path: pathname,
            query: queryString,
            hasExceededLimit,
            firstName,
            lastName,
            ...(data || {}),
          },
        }),
      ).unwrap();

      showToast("Thanks! Our team will reach out soon.", "success");
      setFullName("");
      setPhone("");
      if (closeOnContactSuccess) {
        setIsOpen(false);
      }
    } catch (error) {
      console.error("Lead submit failed:", error);
      showToast("Failed to capture lead. Please try again.", "error");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  return (
    <div className="ff_poppins fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/50 p-0 md:p-4 transition-all duration-300">
      <div className="relative w-full md:max-w-[420px] bg-white rounded-t-[32px] md:rounded-b-[32px] rounded-b-none shadow-[0px_-8px_32px_0px_rgba(0,0,0,0.1)] md:shadow-[0px_8px_32px_0px_rgba(0,0,0,0.1)] border border-gray-100 overflow-y-auto max-h-[92dvh] p-6 pb-[max(2rem,env(safe-area-inset-bottom))] flex flex-col gap-6 animate-fadeIn mt-auto md:mt-0">
        <div className="md:hidden w-12 h-1.5 bg-gray-200 rounded-full mx-auto -mt-2 mb-1" />

        <button
          onClick={() => setIsOpen(false)}
          data-ga-event="anonymous_followup_modal_close"
          className="absolute top-5 right-5 text-gray-500 hover:text-gray-800 bg-gray-50 rounded-full p-1.5 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center gap-2 mt-2 md:mt-0">
          <div className="w-14 h-14 bg-[#9D7AE8]/10 rounded-full flex items-center justify-center">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shadow-[0px_4px_12px_0px_rgba(107,63,200,0.25)]"
              style={{ background: "linear-gradient(135deg, #9D7AE8 0%, #6B3FC8 100%)" }}
            >
              <Lock className="text-white w-4 h-4" />
            </div>
          </div>
          <h3 className="text-[22px] font-bold text-gray-800 mt-2 tracking-tight">
            Continue the conversation
          </h3>
          <p className="text-gray-500 text-xs px-4">
            Share your contact to unlock full access
          </p>
          {hasExceededLimit && (
            <p className="text-[11px] font-medium text-[#CD2C2C] mt-1">
              You have reached your free message limit. Please sign up to
              continue.
            </p>
          )}
        </div>

        <div className="flex bg-[#F8F9FA] p-1.5 rounded-[20px] gap-1">
          <button
            onClick={() => setActiveTab("contact")}
            data-ga-event="anonymous_followup_contact_tab_click"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-[16px] transition-all ${activeTab === "contact" ? "bg-white shadow-[0px_2px_8px_rgba(0,0,0,0.06)] font-semibold text-[#6B3FC8]" : "text-gray-500 font-medium hover:text-gray-700 hover:bg-[#F3F4F6]"}`}
          >
            <Phone size={14} />
            <span className="text-sm">Quick Contact</span>
          </button>
          <button
            onClick={() => setActiveTab("signup")}
            data-ga-event="anonymous_followup_signup_tab_click"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-[16px] transition-all ${activeTab === "signup" ? "bg-white shadow-[0px_2px_8px_rgba(0,0,0,0.06)] font-semibold text-[#6B3FC8]" : "text-gray-500 font-medium hover:text-gray-700 hover:bg-[#F3F4F6]"}`}
          >
            <UserPlus size={14} />
            <span className="text-sm">Sign Up</span>
          </button>
        </div>

        <div className="bg-gradient-to-r from-[#9D7AE8]/5 to-[#6B3FC8]/5 p-4 rounded-2xl flex flex-col gap-2 border border-[#9D7AE8]/10">
          <div className="flex items-center gap-2 font-bold text-xs text-[#374151]">
            <Sparkles size={14} className="text-[#6B3FC8]" />
            Get personalised assistance
          </div>
          <ul className="text-[11px] text-[#4B5563] flex flex-col gap-1.5 list-disc list-inside px-1">
            <li>Instant updates on your queries</li>
            <li>Exclusive offers and insights</li>
            <li>Priority support access</li>
          </ul>
        </div>

        {activeTab === "contact" ? (
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                if (fullNameError) setFullNameError("");
              }}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              placeholder="Enter your full name"
              className="h-[48px] rounded-[16px] border border-[#D1D5DB] px-4 text-[15px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#6B3FC8] focus:ring-1 focus:ring-[#6B3FC8]/20 transition-all"
            />
            {fullNameError && (
              <p className="text-[11px] text-[#CD2C2C] ff_poppins -mt-1">
                {fullNameError}
              </p>
            )}
            <PhoneInput
              className="w-full border border-[#D1D5DB] rounded-[16px] ff_poppins focus-within:border-[#6B3FC8] focus-within:ring-1 focus-within:ring-[#6B3FC8]/20 transition-all overflow-hidden"
              forceDialCode
              defaultCountry="in"
              hideDropdown={true}
              inputClassName="!text-[15px] !h-[48px] !border-none ff_poppins"
              countrySelectorStyleProps={{
                buttonClassName:
                  "!h-[48px] !text-[15px] !bg-white !border-none !rounded-none !px-3 ff_poppins",
              }}
              value={phone}
              onChange={(value) => {
                setPhone(value);
                if (phoneError) setPhoneError("");
              }}
              inputProps={{
                onPaste: (e) => e.preventDefault(),
                onCopy: (e) => e.preventDefault(),
                onCut: (e) => e.preventDefault(),
              }}
            />
            {phoneError && (
              <p className="text-[11px] text-[#CD2C2C] ff_poppins -mt-1">
                {phoneError}
              </p>
            )}
            <button
              onClick={submitLeadFromModal}
              disabled={isSubmittingLead}
              data-ga-event="anonymous_followup_submit_lead_click"
              className="w-full text-white font-semibold py-3.5 rounded-[16px] shadow-[0px_4px_12px_0px_rgba(107,63,200,0.25)] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:hover:scale-100 mt-2"
              style={{ background: "linear-gradient(135deg, #9D7AE8 0%, #6B3FC8 100%)" }}
            >
              {isSubmittingLead ? "Submitting..." : contactButtonText}
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#EEF0F3] bg-white p-3">
            <CommonAuth setShow={setIsOpen} compact />
          </div>
        )}

        <p className="text-[8px] text-gray-400 text-center flex items-center justify-center gap-1 mt-1 uppercase tracking-widest font-medium">
          <Lock size={8} />
          Your information is secure. We respect your privacy
        </p>
      </div>
    </div>
  );
};

export default AnonymousFollowUpModal;
